import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  History,
  Search,
  Filter,
  FileText,
  FileCheck,
  Users,
  GitPullRequest,
  PenLine,
  Settings,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';

const actionIcons = {
  msa_template_created: FileText,
  msa_template_updated: FileText,
  msa_template_deleted: FileText,
  msa_document_created: FileText,
  msa_sent_for_signature: FileText,
  msa_status_updated: FileText,
  msa_signing_link_resent: FileText,
  po_template_created: FileCheck,
  po_template_deleted: FileCheck,
  po_document_created: FileCheck,
  po_sent_for_signature: FileCheck,
  po_signing_link_resent: FileCheck,
  vendor_invited: Users,
  vendor_auto_created: Users,
  vendor_updated: Users,
  vendor_invite_resent: Users,
  vendor_w9_uploaded: Users,
  vendor_document_signed: PenLine,
  vendor_change_request_submitted: GitPullRequest,
  w9_uploaded: Users,
  change_request_created: GitPullRequest,
  change_request_ai_reviewed: GitPullRequest,
  change_request_approved: GitPullRequest,
  change_request_rejected: GitPullRequest,
  document_signed: PenLine,
  company_updated: Settings,
  user_invited: Users,
  role_changed: Users,
};

const actionColors = {
  created: 'bg-emerald-500/10 text-emerald-400',
  updated: 'bg-blue-500/10 text-blue-400',
  deleted: 'bg-rose-500/10 text-rose-400',
  sent: 'bg-amber-500/10 text-amber-400',
  approved: 'bg-emerald-500/10 text-emerald-400',
  rejected: 'bg-rose-500/10 text-rose-400',
  signed: 'bg-violet-500/10 text-violet-400',
  invited: 'bg-blue-500/10 text-blue-400',
  uploaded: 'bg-emerald-500/10 text-emerald-400',
  reviewed: 'bg-blue-500/10 text-blue-400',
};

const getActionColor = (action) => {
  for (const [key, value] of Object.entries(actionColors)) {
    if (action.includes(key)) return value;
  }
  return 'bg-zinc-500/10 text-zinc-400';
};

const formatAction = (action) => {
  return action
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default function AuditTrail() {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await api().get('/audit?limit=500');
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter((log) => {
    const matchesSearch =
      log.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.user_email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesEntity =
      entityFilter === 'all' || log.entity_type.includes(entityFilter);
    return matchesSearch && matchesEntity;
  });

  const exportLogs = () => {
    const csv = [
      ['Timestamp', 'Action', 'Entity Type', 'Entity ID', 'User Email'].join(','),
      ...filteredLogs.map((log) =>
        [
          new Date(log.timestamp).toISOString(),
          log.action,
          log.entity_type,
          log.entity_id,
          log.user_email,
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-trail-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Audit logs exported');
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="h-8 w-48 skeleton" />
        <div className="h-64 skeleton rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-trail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white font-['Manrope'] tracking-tight">
            Audit Trail
          </h1>
          <p className="text-zinc-400 mt-1">
            Complete history of all actions and changes
          </p>
        </div>
        <Button
          onClick={exportLogs}
          variant="outline"
          className="border-zinc-700 hover:bg-zinc-800"
          data-testid="export-logs-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Search by action or user..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-zinc-900 border-zinc-800"
            data-testid="audit-search-input"
          />
        </div>
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-800">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by entity" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">All Entities</SelectItem>
            <SelectItem value="msa">MSA</SelectItem>
            <SelectItem value="po">Purchase Order</SelectItem>
            <SelectItem value="vendor">Vendor</SelectItem>
            <SelectItem value="change_request">Change Request</SelectItem>
            <SelectItem value="company">Company</SelectItem>
            <SelectItem value="user">User</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-900/40 border-zinc-800">
          <CardContent className="pt-4">
            <p className="text-3xl font-bold text-white font-['Manrope']">
              {logs.length}
            </p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">
              Total Events
            </p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/40 border-zinc-800">
          <CardContent className="pt-4">
            <p className="text-3xl font-bold text-white font-['Manrope']">
              {logs.filter((l) => l.action.includes('created')).length}
            </p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">
              Created
            </p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/40 border-zinc-800">
          <CardContent className="pt-4">
            <p className="text-3xl font-bold text-white font-['Manrope']">
              {logs.filter((l) => l.action.includes('signed')).length}
            </p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">
              Signed
            </p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/40 border-zinc-800">
          <CardContent className="pt-4">
            <p className="text-3xl font-bold text-white font-['Manrope']">
              {new Set(logs.map((l) => l.user_email)).size}
            </p>
            <p className="text-xs text-zinc-500 uppercase tracking-wider">
              Active Users
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Logs Timeline */}
      <Card className="bg-zinc-900/40 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg text-zinc-200 font-['Manrope']">
            Activity Log ({filteredLogs.length} events)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredLogs.length > 0 ? (
            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-4">
              {filteredLogs.map((log, index) => {
                const Icon = actionIcons[log.action] || History;
                return (
                  <div
                    key={log.id}
                    className="flex items-start gap-4 pb-4 border-b border-zinc-800/50 last:border-0 last:pb-0"
                    data-testid={`audit-log-${log.id}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-zinc-800/50 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-zinc-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <Badge className={`${getActionColor(log.action)} text-xs`}>
                            {formatAction(log.action)}
                          </Badge>
                          <span className="text-xs text-zinc-500 ml-2 uppercase">
                            {log.entity_type.replace('_', ' ')}
                          </span>
                        </div>
                        <span className="text-xs text-zinc-500 whitespace-nowrap">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-300 mt-1">
                        by <span className="text-zinc-200">{log.user_email}</span>
                      </p>
                      <p className="text-xs text-zinc-500 font-mono mt-1">
                        ID: {log.entity_id.slice(0, 8)}...
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <History className="empty-state-icon" />
              <p className="text-zinc-400">No audit logs found</p>
              <p className="text-zinc-500 text-sm">
                Actions will be logged as you use the platform
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
